import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  FormHelperText,
  Stack,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import moment from "moment";
import PromotionModal from "./ItemDetail";
import { useSelector } from "react-redux";
import {
  AppPromotions_Get_Files,
  CRApp_Promotions_Summary,
} from "./PromotionAction";
import {
  fAlertToast,
  fcheck_null_undefine_replace_empty,
  getStatusStyleV1,
} from "../../Utility/Utilitys";
import { CmpDatePickerField001V1 } from "../../Component/Common/ToolBox/ToolBoxV1";
import {
  DataGridPro,
  gridRowIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

const statusOptions = ["ALL", "ENTER", "POST"];

// DataGrid Columns
const columns = [
  { field: "UId", headerName: "Promo ID", width: 75, align: "center" },
  { field: "NameEN", headerName: "Promo Name EN", width: 150 },
  { field: "NameFL", headerName: "Promo Name FL", width: 150 },
  {
    field: "FromDate",
    headerName: "Start Date",
    width: 90,
    align: "center",
    valueFormatter: ({ value }) => moment(value).format("DD/MM/YYYY"),
  },
  {
    field: "ToDate",
    headerName: "End Date",
    width: 90,
    align: "center",
    valueFormatter: ({ value }) => moment(value).format("DD/MM/YYYY"),
  },
  { field: "Loc", headerName: "Loc", width: 30, align: "center" },
  { field: "Narr", headerName: "Narration", width: 160 },
  {
    field: "NotificationContent",
    headerName: "Notification Content",
    minWidth: 200,
    flex: 1,
  },
  {
    field: "Status",
    headerName: "Status",
    width: 75,
    renderCell: (params) => {
      const style = getStatusStyleV1(params.value);
      return (
        <div
          style={{
            ...style,
            padding: "2px 8px",
            borderRadius: "4px",
            textAlign: "center",
            width: "100%",
            fontWeight: "bold",
          }}
        >
          {params.value}
        </div>
      );
    },
  },
];

const PromoSummary = () => {
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const vRdxuserInfo = useSelector((state) => state.login.value);
  // Dropdown locations
  const locations = useSelector(
    (state) => state.SliceDB.value.Location.LocWithRights
  );
  // State for filters (single object)
  const [filters, setFilters] = useState({
    startDate: moment().subtract(1, "days").toDate(),
    endDate: moment().toDate(),
    location: locations[0]?.Uid,
    status: "ALL",
  });

  // Validation state
  const [errors, setErrors] = useState({
    startDate: false,
    endDate: false,
    location: false,
    status: false,
  });

  const [promoData, setPromoData] = useState([]);
  // State for modal
  const [open, setOpen] = useState(false);
  // State for form inputs
  const initialPromoInputs = {
    promoId: 0,
    promoStartDate: null,
    promoEndDate: null,
    transLocation: [],
    location: "",
    promoNameEn: "",
    promoNameFl: "",
    narration: "",
    promoStatus: "ENTER",
    isActive: true,
    NotificationContent:
      "Enjoy special discounts on your favorite products! Don't miss out—shop now and save big",
    images: ["", "", "", ""], // Initial 4 image inputs
    pdfs: [""], // Initial 1 PDF input
  };
  const [promoInputs, setPromoInputs] = useState(initialPromoInputs);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: false })); // Clear errors on input change
  };

  // Validate Filters
  const validateFilters = () => {
    const newErrors = {
      startDate: !filters.startDate,
      endDate: !filters.endDate,
      location: !filters.location,
      status: !filters.status,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };

  // Handle API Call
  const handleLoadData = async () => {
    if (!validateFilters()) return;

    const filterData = {
      FromDate: moment(filters.startDate).format("MM/DD/YYYY"),
      ToDate: moment(filters.endDate).format("MM/DD/YYYY"),
      Location: filters.location,
      Status: filters.status.toString(),
      UserCd: vRdxuserInfo?.UserID || "",
    };
    const response = await CRApp_Promotions_Summary(
      fcheck_null_undefine_replace_empty(filterData)
    );
    if (response?.status === 200 && response?.data?.recordset) {
      setPromoData(response?.data?.recordset); // Fallback to dummy data
    } else {
      setPromoData([]); // Use dummy data on API error
    }
  };

  // Open modal on row click
  const handleRowClick = async (params) => {
    setOpen(true);
    setPromoInputs((prev) => ({
      ...prev,
      promoId: params.row.UId || 0,
      promoStartDate: params.row.FromDate,
      promoEndDate: params.row.ToDate,
      transLocation: params.row.LocationCSV
        ? params.row.LocationCSV.split(",") // Converts "1,2,3" => ["1", "2", "3"]
        : [],
      location: params.row.Loc,
      promoNameEn: params.row.NameEN,
      promoNameFl: params.row.NameFL,
      narration: params.row.Narr,
      promoStatus: params.row.Status,
      isActive: params.row.Active === "Y",
      NotificationContent: params.row.NotificationContent,
    }));
    const response = await AppPromotions_Get_Files(
      fcheck_null_undefine_replace_empty({
        promoID: params.row.UId,
      })
    );
    if (response?.status === 200 && response?.data) {
      const { images, pdfs } = response.data;

      let formattedImages = [],
        formattedPdfs = [];
      if (images.length === 0) {
        formattedImages = ["", "", "", ""];
      } else {
        formattedImages = images.length > 0 && images?.map((base64) => base64);
      }
      if (pdfs.length === 0) {
        formattedPdfs = [""];
      } else {
        formattedPdfs = pdfs.length > 0 && pdfs.map((base64) => base64);
      }
      setPromoInputs((prev) => ({
        ...prev,
        images: formattedImages,
        pdfs: formattedPdfs,
      }));
    } else if (response?.response?.status === 500) {
      fAlertToast("FAILED", response?.response?.data?.error);
    } else {
      fAlertToast("FAILED", "Failed to retrieve files. No data found.");
    }
  };
  // Function to Reset State
  const handleNew = () => {
    setPromoInputs(initialPromoInputs);
    setErrors({});
  };
  const handleModalClose = () => {
    setOpen(false);
    handleLoadData(); // Refresh data when modal closes
    handleNew();
  };

  const handleExport = () => {
    apiRef.current.exportDataAsCsv({
      getRowsToExport: ({ apiRef }) => gridRowIdsSelector(apiRef),
      includeHeaders: true,
      utf8WithBom: true,
    });
  };
  const handlePrint = () => {
    apiRef.current.exportDataAsPrint();
  };

  // Fetch locations on mount
  useEffect(() => {
    handleLoadData();
    document.getElementById("startDate")?.focus();
  }, []);

  return (
    <Box
      sx={{
        p: 1,
        width: "100%",
      }}
    >
      <div className="mt-2 ms-3 mb-3">
        <span className="breadcrumb-not-active">App Promotion</span>
      </div>
      <Grid container spacing={1} alignItems="center">
        {/* Start Date */}
        <Grid item xs={6} sm={3} md={2.5} lg={1.5}>
          <CmpDatePickerField001V1
            xLabelText="Start Date"
            xValue={filters.startDate}
            xOnChange={(newValue) => {
              handleFilterChange("startDate", newValue?.$d ?? newValue);
            }}
          />
        </Grid>
        {/* End Date */}
        <Grid item xs={6} sm={3} md={2.5} lg={1.5}>
          <CmpDatePickerField001V1
            xLabelText="End Date"
            xValue={filters.endDate}
            xOnChange={(newValue) => {
              handleFilterChange("endDate", newValue?.$d ?? newValue);
            }}
          />
        </Grid>
        {/* Location Dropdown */}
        <Grid item xs={12} sm={6} md={4.5} lg={4}>
          <FormControl fullWidth size="small" error={errors?.location}>
            <InputLabel shrink id="summary-location-label">
              Trans Location
            </InputLabel>
            <Select
              label="Location"
              labelId="summary-location-label"
              value={filters.location}
              onChange={(e) => handleFilterChange("location", e.target.value)}
            >
              {locations?.map((loc) => (
                <MenuItem className="menuSm" key={loc.Uid} value={loc.Uid}>
                  {loc.Name1}
                </MenuItem>
              ))}
            </Select>
            {errors?.location && (
              <FormHelperText>Location is required</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={4.5} sm={3} md={2.5} lg={1.5}>
          <FormControl fullWidth size="small" sx={{ minHeight: "30px" }}>
            <InputLabel sx={{ fontSize: "12px" }}>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
              sx={{ height: "30px", minHeight: "30px" }}
            >
              <MenuItem className="menuSm" value="" disabled>
                Select Status
              </MenuItem>
              {statusOptions?.map((option) => (
                <MenuItem key={option} value={option} className="menuSm">
                  {option}
                </MenuItem>
              ))}
            </Select>
            {errors?.status && (
              <FormHelperText>Status is required</FormHelperText>
            )}
          </FormControl>
        </Grid>
        {/* Buttons */}
        <Grid
          item
          xs={7.5}
          sm={9}
          md={12}
          lg={3.5}
          container
          justifyContent="flex-end"
        >
          <Stack direction="row" spacing={1} flexWrap={"wrap"}>
            <Button variant="contained" size="small" onClick={handleLoadData}>
              Load
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                handleNew();
                setOpen(true);
              }}
            >
              New
            </Button>
            <Tooltip title="Export CSV">
              <IconButton color="primary" onClick={handleExport} size="small">
                <DownloadOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton color="primary" onClick={handlePrint} size="small">
                <PrintOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>
      <Box
        sx={{
          height: {
            xs: "65vh",
            sm: "73vh",
            lg: "78vh",
          },
          mt: 1,
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          rows={promoData ?? []}
          columns={columns}
          headerHeight={40}
          rowHeight={30}
          getRowId={(row) => row.UId}
          onRowDoubleClick={handleRowClick}
          hideFooter={promoData?.length < 100}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? "mui-even-row"
              : "mui-odd-row"
          }
          sx={{
            border: 1,
            borderColor: "primary.light",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.primary.main,
              color: "white",
            },
          }}
        />
      </Box>
      {open && (
        <PromotionModal
          open={open}
          setOpen={setOpen}
          promoData={promoInputs}
          setPromoData={setPromoInputs}
          handleNew={handleNew}
          locations={locations}
          vRdxuserInfo={vRdxuserInfo}
          onClose={handleModalClose}
        />
      )}
    </Box>
  );
};

export default PromoSummary;
